import { DocsImageRefType, } from "../../exports";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Users Area
export class AreaResources {
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(dataCore) {
        this.dataCore = dataCore;
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Read
    /** Fetches all remote asset resources in the version
     * @param from - Remote version to fetch from
     * @returns All image resources in the specified version
     */
    async getAssetResources(from) {
        return this.dataCore.assetResources(from.designSystemId, from.versionId);
    }
    /** Fetches all remote font resources in the version
     * @param from - Remote version to fetch from
     * @returns All font resources in the specified version
     */
    async getFontResources(from) {
        return this.dataCore.fontResources(from.designSystemId, from.versionId);
    }
    /** Fetches all Figma frames that were rendered font resources in the version
     * @param from - Remote version to fetch from
     * @returns All font resources in the specified version
     */
    async getFigmaFrames(from) {
        return this.dataCore.figmaFrames(from.designSystemId, from.versionId);
    }
    /** Fetches all Figma frames that were rendered font resources in the version (V2 of the API)
     * @param from - Remote version to fetch from
     * @returns All font resources in the specified version
     */
    async getFigmaFramesV2(from) {
        return this.dataCore.figmaFramesV2(from.designSystemId, from.versionId);
    }
    /**
     * @param from - Remote version to fetch from
     * @param frameReferenceId  - Unique stable identifier of the Figma frame (Supernova id, not Figma id)
     * @returns URL of the hosted image of the Figma frame that is stable across updates
     */
    getFigmaFrameHostedUrl(from, frameReferenceId) {
        const resourceFragment = `figma-nodes/${frameReferenceId}/image-url`;
        return this.dataCore
            .getBridgeInternal()
            .getVersionEndpoint(from.designSystemId, from.versionId, resourceFragment);
    }
    /** Retrieves stable URL
     * @param from - Remote version to fetch from
     * @param frameReferenceId  - Unique stable identifier of the Figma frame (Supernova id, not Figma id)
     * @returns URL of the hosted image resource, either uploaded file or Figma frame
     */
    getImageRefHostedUrl(from, imageRef) {
        var _a;
        if (!imageRef) {
            return undefined;
        }
        if (imageRef.type === DocsImageRefType.figmaNode && imageRef.figmaNode) {
            return this.getFigmaFrameHostedUrl(from, imageRef.figmaNode.frameReferenceId);
        }
        if (imageRef.type === DocsImageRefType.resource && ((_a = imageRef.resource) === null || _a === void 0 ? void 0 : _a.url)) {
            return imageRef.resource.url;
        }
        return undefined;
    }
    /** Fetches all files in the version or a subset of them based on the persistentIds
     * @param from - Remote version to fetch from
     * @param persistentIds - Optional array of persistentIds to filter the files by
     * @returns All files in the specified version or a subset of them based on the persistentIds
     */
    async getFiles(from) {
        return this.dataCore.files(from.designSystemId, from.versionId);
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Create/Update
    /** Uploads new asset resource */
    async uploadAssetResource(from, asset) {
        return this.dataCore.uploadAssetResource(from.designSystemId, from.versionId, asset);
    }
    /** Uploads new font resource */
    async uploadFontResource(from, font) {
        return this.dataCore.uploadFontResource(from.designSystemId, from.versionId, font);
    }
    /** Uploads new font resources */
    async uploadFontResources(from, fonts) {
        return this.dataCore.uploadFontResources(from.designSystemId, from.versionId, fonts);
    }
    async initializeFileUpload(from, fileUploadItems) {
        return this.dataCore.initializeFileUpload(from.designSystemId, from.versionId, fileUploadItems);
    }
    async finalizeFileUpload(from, fileIds) {
        return this.dataCore.finalizeFileUpload(from.designSystemId, from.versionId, fileIds);
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Delete
    /** Deletes single resource of any type */
    async deleteResource(from, resourceId) {
        return this.dataCore.deleteResource(from.designSystemId, from.versionId, resourceId);
    }
    /** Deletes multiple resource of any type */
    async deleteResources(from, resourceIds) {
        return this.dataCore.deleteResources(from.designSystemId, from.versionId, resourceIds);
    }
}
